import React, { useState } from 'react'
import {
  Dialog,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Stack,
  useTheme,
  IconButton,
} from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts'
import {
  demandPatterns,
  distributionTypes,
  getPatternPreview,
  getDistributionPreview,
} from './DemandPatternService'
import CloseIcon from '@mui/icons-material/Close'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import BarChartIcon from '@mui/icons-material/BarChart'
import { useCurrency } from '../contexts/CurrencyContext'

// ─── pill selector for pattern / distribution type ───────────────────────────
function PillSelector({ options, value, onChange, accent }) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: '6px' }}>
      {options.map(opt => {
        const active = value === opt.id
        return (
          <Box
            key={opt.id}
            onClick={() => onChange(opt.id)}
            title={opt.description}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1.25, py: 0.5, borderRadius: '6px',
              cursor: 'pointer', userSelect: 'none',
              border: `1px solid ${active ? accent : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)')}`,
              bgcolor: active
                ? `${accent}18`
                : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
              transition: 'all 0.12s',
              '&:hover': {
                borderColor: accent,
                bgcolor: `${accent}10`,
              },
            }}
          >
            <Typography sx={{ fontSize: '12px', lineHeight: 1, userSelect: 'none' }}>{opt.icon}</Typography>
            <Typography sx={{
              fontSize: '11px', fontWeight: active ? 700 : 500,
              color: active ? accent : (isDark ? '#94a3b8' : '#64748b'),
              letterSpacing: '0.01em',
            }}>
              {opt.name}
            </Typography>
            {active && (
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: accent, ml: 0.25 }} />
            )}
          </Box>
        )
      })}
    </Stack>
  )
}

// ─── tiny section label ───────────────────────────────────────────────────────
function SectionLabel({ children, color = '#6366f1' }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
      <Box sx={{ width: 2, height: 10, borderRadius: 1, bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{
        fontSize: '9.5px', fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '0.08em', color,
      }}>
        {children}
      </Typography>
    </Box>
  )
}

// ─── main dialog ─────────────────────────────────────────────────────────────
function AdvancedDemandDialog({ open, onClose, onSave, demand, nodes }) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { currency } = useCurrency()

  // design tokens
  const bg        = isDark ? '#0d1626' : '#f8fafc'
  const surface   = isDark ? '#131f35' : '#ffffff'
  const surfaceAlt = isDark ? '#0a111e' : '#f1f5f9'
  const border    = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)'
  const textPrimary   = isDark ? '#f1f5f9' : '#0f172a'
  const textSecondary = isDark ? '#64748b' : '#94a3b8'
  const accent    = '#6366f1'
  const accentAlt = '#f59e0b'

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px', fontSize: '12.5px',
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)' },
      '&.Mui-focused fieldset': { borderColor: accent },
    },
    '& .MuiInputLabel-root': { fontSize: '12px', color: textSecondary },
    '& .MuiFormHelperText-root': { fontSize: '10px', mt: 0.25 },
    '& .MuiSelect-select': { fontSize: '12.5px' },
  }

  const [demandData, setDemandData] = useState(() => {
    if (demand) {
      return {
        ...demand,
        pattern: demand.pattern || 'constant',
        patternParams: demand.patternParams || { interval: 1 },
        distribution_type: demand.distribution_type || 'fixed',
        distribution_params: demand.distribution_params || {},
      }
    }
    const targetNode = nodes.find(n => n.data.nodeType === 'distributor' || n.data.nodeType === 'retailer') || nodes[0]
    return {
      id: `demand_${Date.now()}`,
      name: 'New Demand',
      target_node: targetNode?.id || '',
      pattern: 'constant',
      patternParams: { interval: 1 },
      arrival_interval: 1,
      distribution_type: 'fixed',
      distribution_params: {},
      order_quantity: 400,
      delivery_cost: 10,
      lead_time: 5,
    }
  })

  const [arrivalPreview, setArrivalPreview] = useState([])
  const [quantityPreview, setQuantityPreview] = useState([])

  React.useEffect(() => {
    if (open && demand) {
      setDemandData({
        ...demand,
        pattern: demand.pattern || 'constant',
        patternParams: demand.patternParams || { interval: 1 },
        distribution_type: demand.distribution_type || 'fixed',
        distribution_params: demand.distribution_params || {},
      })
    }
  }, [open, demand])

  React.useEffect(() => {
    if (demandData.pattern) {
      setArrivalPreview(getPatternPreview(demandData.pattern, demandData.patternParams))
    }
  }, [demandData.pattern, JSON.stringify(demandData.patternParams)])

  React.useEffect(() => {
    if (demandData.distribution_type) {
      setQuantityPreview(getDistributionPreview(
        demandData.distribution_type,
        demandData.distribution_params,
        demandData.order_quantity,
      ))
    }
  }, [demandData.distribution_type, JSON.stringify(demandData.distribution_params), demandData.order_quantity])

  React.useEffect(() => {
    if (open) {
      if (demandData.pattern)
        setArrivalPreview(getPatternPreview(demandData.pattern, demandData.patternParams))
      if (demandData.distribution_type)
        setQuantityPreview(getDistributionPreview(demandData.distribution_type, demandData.distribution_params, demandData.order_quantity))
    }
  }, [open])

  const handlePatternChange = newPattern => {
    const defaults = {
      constant:  { interval: 1 },
      random:    { avgInterval: 1, variance: 0.2 },
      seasonal:  { baseInterval: 1, amplitude: 0.5, period: 10 },
      trending:  { startInterval: 1, endInterval: 2, duration: 30 },
      burst:     { normalInterval: 1, burstInterval: 0.2, burstFrequency: 5 },
    }
    setDemandData({ ...demandData, pattern: newPattern, patternParams: defaults[newPattern] || {}, arrival_interval: defaults[newPattern]?.interval || 1 })
  }

  const handleDistributionChange = newDist => {
    const defaults = {
      fixed:       {},
      normal:      { mean: demandData.order_quantity, std_dev: 50 },
      uniform:     { min: demandData.order_quantity - 100, max: demandData.order_quantity + 100 },
      poisson:     { lambda: demandData.order_quantity },
      exponential: { scale: demandData.order_quantity },
    }
    setDemandData({ ...demandData, distribution_type: newDist, distribution_params: defaults[newDist] || {} })
  }

  const handlePatternParamChange = (param, value) =>
    setDemandData({ ...demandData, patternParams: { ...demandData.patternParams, [param]: parseFloat(value) || 0 } })

  const handleDistParamChange = (param, value) =>
    setDemandData({ ...demandData, distribution_params: { ...demandData.distribution_params, [param]: parseFloat(value) || 0 } })

  const handleSave = () => {
    onSave({
      id: demandData.id,
      name: demandData.name,
      target_node: demandData.target_node,
      pattern: demandData.pattern,
      patternParams: demandData.patternParams,
      arrival_interval:
        demandData.patternParams.interval ||
        demandData.patternParams.avgInterval ||
        demandData.patternParams.baseInterval ||
        demandData.arrival_interval || 1,
      distribution_type: demandData.distribution_type,
      distribution_params: demandData.distribution_params,
      order_quantity: demandData.order_quantity,
      delivery_cost: demandData.delivery_cost,
      lead_time: demandData.lead_time,
    })
    onClose()
  }

  const patternOptions = Object.values(demandPatterns)
  const distOptions    = Object.values(distributionTypes)

  const chartProps = {
    cartesianGrid: { strokeDasharray: '3 3', stroke: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)' },
    tick: { fontSize: 9, fill: textSecondary },
    tooltip: {
      contentStyle: {
        backgroundColor: surface,
        border: `1px solid ${border}`,
        borderRadius: '8px',
        fontSize: '11px',
        color: textPrimary,
        boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.1)',
      },
    },
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: bg,
          borderRadius: '14px',
          border: `1px solid ${border}`,
          boxShadow: isDark ? '0 24px 80px rgba(0,0,0,0.7)' : '0 24px 80px rgba(0,0,0,0.18)',
          maxHeight: '88vh',
          overflow: 'hidden',
        },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{
        px: 3, height: 52, flexShrink: 0,
        borderBottom: `1px solid ${border}`,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '8px',
          bgcolor: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ShowChartIcon sx={{ fontSize: 15, color: accent }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: textPrimary, letterSpacing: '-0.01em' }}>
            {demand ? 'Edit' : 'Configure'} Advanced Demand Pattern
          </Typography>
          <Typography sx={{ fontSize: '10.5px', color: textSecondary, mt: 0.15 }}>
            Arrival timing &amp; quantity distribution
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ width: 28, height: 28, color: textSecondary }}>
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {/* ── Scrollable body ─────────────────────────────────────────────── */}
      <Box sx={{
        overflowY: 'auto', p: 2.5,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: border, borderRadius: 2 },
      }}>
        {/* Basic info row */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
          <TextField
            label="Demand Name"
            value={demandData.name}
            onChange={e => setDemandData({ ...demandData, name: e.target.value })}
            size="small" fullWidth sx={fieldSx}
          />
          <FormControl fullWidth size="small" sx={fieldSx}>
            <InputLabel>Target Node</InputLabel>
            <Select
              value={demandData.target_node}
              label="Target Node"
              onChange={e => setDemandData({ ...demandData, target_node: e.target.value })}
            >
              {nodes.map(node => (
                <MenuItem key={node.id} value={node.id}>
                  {node.data.label} ({node.data.nodeType})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Two-column sections */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>

          {/* ── Left: Arrival Pattern ──────────────────────────────────── */}
          <Box sx={{
            flex: 1, borderRadius: '12px', border: `1px solid ${border}`,
            bgcolor: surface, overflow: 'hidden',
          }}>
            <Box sx={{ height: 3, bgcolor: accent }} />
            <Box sx={{ p: 2 }}>
              <SectionLabel color={accent}>Order Arrival Pattern</SectionLabel>

              <PillSelector
                options={patternOptions}
                value={demandData.pattern}
                onChange={handlePatternChange}
                accent={accent}
              />

              {/* Pattern parameters */}
              <Box sx={{ mt: 2, p: 1.5, borderRadius: '8px', bgcolor: surfaceAlt }}>
                <Typography sx={{ fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: textSecondary, mb: 1.25 }}>
                  Parameters
                </Typography>
                <Stack spacing={1.25}>
                  {demandData.pattern === 'constant' && (
                    <TextField label="Interval (days)" type="number"
                      value={demandData.patternParams.interval || 1}
                      onChange={e => handlePatternParamChange('interval', e.target.value)}
                      size="small" fullWidth sx={fieldSx}
                      inputProps={{ step: '0.1', min: 0.1 }}
                    />
                  )}
                  {demandData.pattern === 'random' && (
                    <Stack direction="row" spacing={1}>
                      <TextField label="Avg Interval" type="number"
                        value={demandData.patternParams.avgInterval || 1}
                        onChange={e => handlePatternParamChange('avgInterval', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '0.1', min: 0.1 }}
                      />
                      <TextField label="Variance (0–1)" type="number"
                        value={demandData.patternParams.variance || 0.2}
                        onChange={e => handlePatternParamChange('variance', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '0.1', min: 0, max: 1 }}
                      />
                    </Stack>
                  )}
                  {demandData.pattern === 'seasonal' && (
                    <Stack direction="row" spacing={1}>
                      <TextField label="Base Interval" type="number"
                        value={demandData.patternParams.baseInterval || 1}
                        onChange={e => handlePatternParamChange('baseInterval', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '0.1', min: 0.1 }}
                      />
                      <TextField label="Amplitude" type="number"
                        value={demandData.patternParams.amplitude || 0.5}
                        onChange={e => handlePatternParamChange('amplitude', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '0.1', min: 0 }}
                      />
                      <TextField label="Period (days)" type="number"
                        value={demandData.patternParams.period || 10}
                        onChange={e => handlePatternParamChange('period', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '1', min: 1 }}
                      />
                    </Stack>
                  )}
                  {demandData.pattern === 'trending' && (
                    <Stack direction="row" spacing={1}>
                      <TextField label="Start Interval" type="number"
                        value={demandData.patternParams.startInterval || 1}
                        onChange={e => handlePatternParamChange('startInterval', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '0.1', min: 0.1 }}
                      />
                      <TextField label="End Interval" type="number"
                        value={demandData.patternParams.endInterval || 2}
                        onChange={e => handlePatternParamChange('endInterval', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '0.1', min: 0.1 }}
                      />
                      <TextField label="Duration (days)" type="number"
                        value={demandData.patternParams.duration || 30}
                        onChange={e => handlePatternParamChange('duration', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '1', min: 1 }}
                      />
                    </Stack>
                  )}
                  {demandData.pattern === 'burst' && (
                    <Stack direction="row" spacing={1}>
                      <TextField label="Normal Interval" type="number"
                        value={demandData.patternParams.normalInterval || 1}
                        onChange={e => handlePatternParamChange('normalInterval', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '0.1', min: 0.1 }}
                      />
                      <TextField label="Burst Interval" type="number"
                        value={demandData.patternParams.burstInterval || 0.2}
                        onChange={e => handlePatternParamChange('burstInterval', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '0.1', min: 0.1 }}
                      />
                      <TextField label="Burst Frequency" type="number"
                        value={demandData.patternParams.burstFrequency || 5}
                        onChange={e => handlePatternParamChange('burstFrequency', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ step: '1', min: 1 }}
                      />
                    </Stack>
                  )}
                </Stack>
              </Box>

              {/* Arrival chart */}
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                  <ShowChartIcon sx={{ fontSize: 12, color: accent }} />
                  <Typography sx={{ fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: textSecondary }}>
                    Arrival Preview
                  </Typography>
                </Stack>
                <ResponsiveContainer width="100%" height={120}>
                  <LineChart data={arrivalPreview}>
                    <CartesianGrid {...chartProps.cartesianGrid} />
                    <XAxis dataKey="x" tick={chartProps.tick} />
                    <YAxis tick={chartProps.tick} width={28} />
                    <Tooltip {...chartProps.tooltip} />
                    <Line type="monotone" dataKey="y" stroke={accent} strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Box>

          {/* ── Right: Quantity Distribution ───────────────────────────── */}
          <Box sx={{
            flex: 1, borderRadius: '12px', border: `1px solid ${border}`,
            bgcolor: surface, overflow: 'hidden',
          }}>
            <Box sx={{ height: 3, bgcolor: accentAlt }} />
            <Box sx={{ p: 2 }}>
              <SectionLabel color={accentAlt}>Quantity Distribution</SectionLabel>

              <PillSelector
                options={distOptions}
                value={demandData.distribution_type}
                onChange={handleDistributionChange}
                accent={accentAlt}
              />

              {/* Distribution parameters */}
              <Box sx={{ mt: 2, p: 1.5, borderRadius: '8px', bgcolor: surfaceAlt }}>
                <Typography sx={{ fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: textSecondary, mb: 1.25 }}>
                  Parameters
                </Typography>
                <Stack spacing={1.25}>
                  {demandData.distribution_type === 'fixed' && (
                    <TextField label="Fixed Quantity (units)" type="number"
                      value={demandData.order_quantity}
                      onChange={e => setDemandData({ ...demandData, order_quantity: parseInt(e.target.value) || 0 })}
                      size="small" fullWidth sx={fieldSx} inputProps={{ min: 1 }}
                    />
                  )}
                  {demandData.distribution_type === 'normal' && (
                    <Stack direction="row" spacing={1}>
                      <TextField label="Mean (μ)" type="number"
                        value={demandData.distribution_params.mean || demandData.order_quantity}
                        onChange={e => handleDistParamChange('mean', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ min: 0 }}
                      />
                      <TextField label="Std Dev (σ)" type="number"
                        value={demandData.distribution_params.std_dev || 50}
                        onChange={e => handleDistParamChange('std_dev', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ min: 0 }}
                      />
                    </Stack>
                  )}
                  {demandData.distribution_type === 'uniform' && (
                    <Stack direction="row" spacing={1}>
                      <TextField label="Min Quantity" type="number"
                        value={demandData.distribution_params.min || demandData.order_quantity - 100}
                        onChange={e => handleDistParamChange('min', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ min: 0 }}
                      />
                      <TextField label="Max Quantity" type="number"
                        value={demandData.distribution_params.max || demandData.order_quantity + 100}
                        onChange={e => handleDistParamChange('max', e.target.value)}
                        size="small" fullWidth sx={fieldSx} inputProps={{ min: 0 }}
                      />
                    </Stack>
                  )}
                  {demandData.distribution_type === 'poisson' && (
                    <TextField label="Lambda (λ) — Average Rate" type="number"
                      value={demandData.distribution_params.lambda || demandData.order_quantity}
                      onChange={e => handleDistParamChange('lambda', e.target.value)}
                      size="small" fullWidth sx={fieldSx} inputProps={{ min: 0 }}
                      helperText="Expected events per interval"
                    />
                  )}
                  {demandData.distribution_type === 'exponential' && (
                    <TextField label="Scale (β) — Mean Time" type="number"
                      value={demandData.distribution_params.scale || demandData.order_quantity}
                      onChange={e => handleDistParamChange('scale', e.target.value)}
                      size="small" fullWidth sx={fieldSx} inputProps={{ min: 0 }}
                      helperText="Average time between events"
                    />
                  )}
                </Stack>
              </Box>

              {/* Quantity chart */}
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                  <BarChartIcon sx={{ fontSize: 12, color: accentAlt }} />
                  <Typography sx={{ fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: textSecondary }}>
                    Distribution Preview
                  </Typography>
                </Stack>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={quantityPreview}>
                    <CartesianGrid {...chartProps.cartesianGrid} />
                    <XAxis dataKey="x" tick={chartProps.tick} />
                    <YAxis tick={chartProps.tick} width={28} />
                    <Tooltip {...chartProps.tooltip} />
                    <Bar dataKey="y" fill={accentAlt} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Box>
        </Stack>

        {/* Order details */}
        <Box sx={{ borderRadius: '12px', border: `1px solid ${border}`, bgcolor: surface, p: 2 }}>
          <SectionLabel color="#10b981">Order Details</SectionLabel>
          <Stack direction="row" spacing={1.5}>
            <TextField
              label={`Delivery Cost (${currency.symbol})`}
              type="number"
              value={demandData.delivery_cost}
              onChange={e => setDemandData({ ...demandData, delivery_cost: parseFloat(e.target.value) })}
              size="small" fullWidth sx={fieldSx} inputProps={{ step: '0.01', min: 0 }}
            />
            <TextField
              label="Lead Time (days)"
              type="number"
              value={demandData.lead_time}
              onChange={e => setDemandData({ ...demandData, lead_time: parseFloat(e.target.value) })}
              size="small" fullWidth sx={fieldSx} inputProps={{ step: '0.1', min: 0 }}
            />
          </Stack>
        </Box>
      </Box>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <Box sx={{
        px: 2.5, py: 1.75, flexShrink: 0,
        borderTop: `1px solid ${border}`,
        display: 'flex', justifyContent: 'flex-end', gap: 1,
      }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none', fontSize: '12.5px', fontWeight: 600,
            color: textSecondary, px: 2, borderRadius: '8px',
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
            px: 2.5, borderRadius: '8px', letterSpacing: '-0.01em',
            bgcolor: accent, boxShadow: 'none',
            '&:hover': { bgcolor: '#4f46e5', boxShadow: 'none' },
          }}
        >
          Save Pattern
        </Button>
      </Box>
    </Dialog>
  )
}

export default AdvancedDemandDialog
